document.getElementById("js-header");
document.getElementById("js-hamburgerBtn");
document.getElementById("js-fullScreenOverlay");
document.getElementById("js-floatingMenu");
document.getElementById("js-toTop");
document.getElementById("js-globalNav");
class OriginalDetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector("details");
    this.summary = this.details.querySelector("summary");
    this.content = this.summary.nextElementSibling;
    this.summary.addEventListener("click", this.toggleDetails.bind(this));
  }
  toggleDetails(event) {
    event.preventDefault();
    if (this.details.hasAttribute("open")) {
      this.closeDetails();
    } else {
      this.openDetails();
    }
  }
  openDetails() {
    this.details.setAttribute("open", "true");
    this.details.classList.add("is-open");
    this.content.animate(this.openingAnimation(), this.animationSettings());
  }
  closeDetails() {
    this.details.classList.remove("is-open");
    const closingAnim = this.content.animate(this.closingAnimation(), this.animationSettings());
    closingAnim.onfinish = () => {
      this.details.removeAttribute("open");
    };
  }
  openingAnimation() {
    return [
      { height: "0px", opacity: 0 },
      { height: `${this.content.scrollHeight}px`, opacity: 1 }
    ];
  }
  closingAnimation() {
    return [
      { height: `${this.content.scrollHeight}px`, opacity: 1 },
      { height: "0px", opacity: 0 }
    ];
  }
  animationSettings() {
    return {
      duration: 300,
      easing: "ease"
    };
  }
}
customElements.define("original-details-disclosure", OriginalDetailsDisclosure);
class OriginalDialog extends HTMLElement {
  constructor() {
    super();
    this.dialogElement = this.querySelector(".js-dialog");
    this.toggleButtons = this.querySelectorAll(".js-dialogToggle");
    this.dialogElement.addEventListener("click", (event) => {
      if (event.target === this.dialogElement) {
        this.toggleDialog();
      }
    });
    this.toggleButtons.forEach((button) => {
      button.addEventListener("click", this.toggleDialog.bind(this));
    });
  }
  /**
   * Toggle dialog visibility based on its current state
   */
  toggleDialog() {
    if (this.isDialogOpen()) {
      this.closeDialog();
    } else {
      this.openDialog();
    }
  }
  /**
   * Check if the dialog is currently open
   * @returns {Boolean} True if the dialog is open
   */
  isDialogOpen() {
    return this.dialogElement.hasAttribute("open");
  }
  /**
   * Open the dialog and add necessary event listeners
   */
  openDialog() {
    if (typeof this.dialogElement.showModal === "function") {
      this.dialogElement.showModal();
    } else {
      console.error("The <dialog> API is not supported by this browser");
    }
  }
  /**
  * Close the dialog and remove event listeners
  */
  closeDialog() {
    this.dialogElement.close();
  }
}
customElements.define("original-dialog", OriginalDialog);
class OriginalFaq extends HTMLElement {
  constructor() {
    super();
    this._items = [];
  }
  connectedCallback() {
    this._items = Array.from(this.querySelectorAll(".js-faq-item"));
    this._items.forEach((details) => this._setupItem(details));
  }
  _setupItem(details) {
    const summary = details.querySelector(".js-faq-question");
    const wrapper = details.querySelector(".js-faq-answer-wrapper");
    if (!summary || !wrapper) return;
    const setHeight = (height) => {
      wrapper.style.height = height === "" ? "" : height + "px";
    };
    const setInitialState = () => {
      if (details.open) {
        details.classList.add("is-expanded");
        wrapper.classList.add("is-open");
        setHeight("");
      } else {
        details.classList.remove("is-expanded");
        wrapper.classList.remove("is-open");
        wrapper.classList.remove("is-collapsing");
        setHeight(0);
      }
    };
    setInitialState();
    summary.addEventListener("click", (e) => {
      e.preventDefault();
      if (wrapper.classList.contains("is-collapsing")) return;
      if (details.open) {
        this._close(details, wrapper, setHeight);
      } else {
        this._open(details, wrapper, setHeight);
      }
    });
  }
  _close(details, wrapper, setHeight) {
    details.classList.remove("is-expanded");
    const startHeight = wrapper.offsetHeight;
    wrapper.style.height = startHeight + "px";
    wrapper.offsetHeight;
    wrapper.classList.add("is-collapsing");
    wrapper.classList.remove("is-open");
    setHeight(0);
    wrapper.addEventListener(
      "transitionend",
      () => {
        wrapper.classList.remove("is-collapsing");
        setHeight("");
        details.removeAttribute("open");
      },
      { once: true }
    );
  }
  _open(details, wrapper, setHeight) {
    details.setAttribute("open", "");
    details.classList.add("is-expanded");
    wrapper.classList.add("is-collapsing");
    setHeight(0);
    wrapper.offsetHeight;
    const endHeight = wrapper.scrollHeight;
    setHeight(endHeight);
    wrapper.addEventListener(
      "transitionend",
      () => {
        wrapper.classList.remove("is-collapsing");
        wrapper.classList.add("is-open");
        setHeight("");
      },
      { once: true }
    );
  }
}
customElements.define("original-faq", OriginalFaq);
class OriginalModal extends HTMLElement {
  constructor() {
    super();
    this.modalElement = this.querySelector(".js-modal");
    this.toggleButtons = this.querySelectorAll(".js-modalToggle");
    this.previousScrollPosition = 0;
    this.toggleButtons.forEach((button) => {
      button.addEventListener("click", this.toggleModal.bind(this));
    });
  }
  /**
   * Toggle modal visibility based on its current state
   */
  toggleModal() {
    if (this.isModalOpen()) {
      this.closeModal();
    } else {
      this.openModal();
    }
  }
  /**
   * Check if the modal is currently open
   * @returns {Boolean} True if the modal is open
   */
  isModalOpen() {
    return this.hasAttribute("open");
  }
  /**
   * Open the modal and add necessary event listeners
   */
  openModal() {
    this.setAttribute("open", "true");
    window.addEventListener("keydown", this.handleKeyboardFocus.bind(this), { capture: true });
    document.addEventListener("touchmove", this.preventScroll, { passive: false });
    this.fixScrollLock(this.modalElement);
    this.setFocusToFirstButton(this.modalElement);
  }
  /**
   * Close the modal and remove event listeners
   */
  closeModal() {
    this.removeAttribute("open");
    window.removeEventListener("keydown", this.handleKeyboardFocus.bind(this), { capture: true });
    document.removeEventListener("touchmove", this.preventScroll);
    this.removeScrollLockFix(this.modalElement);
    this.toggleButtons[0].focus();
  }
  /**
   * Handle keyboard interactions inside the modal
   */
  handleKeyboardFocus(event) {
    this.controlModalFocus(event, this.modalElement, this.closeModal.bind(this));
  }
  /**
   * Set focus to either the first or last button inside the modal
   * @param {DOM} parentElement The modal element
   * @param {Boolean} isFirstFocus Whether to focus on the first or last button
   */
  setFocusToFirstButton(parentElement, isFirstFocus = true) {
    if (!parentElement) {
      throw new Error("Modal element not found");
    }
    const focusableElements = this.getFocusableElements(parentElement);
    if (focusableElements.length > 0) {
      focusableElements[isFirstFocus ? 0 : focusableElements.length - 1].focus();
    }
  }
  /**
   * Control modal keyboard focus behavior (e.g., trapping focus, handling Escape)
   * @param {Event} event
   * @param {DOM} parentElement The modal element
   * @param {Function} onClose Function to close the modal
   */
  controlModalFocus(event, parentElement, onClose) {
    if (!parentElement) return;
    switch (event.code) {
      case "Enter":
      case "Space":
        break;
      case "Escape":
        onClose();
        break;
      case "Tab": {
        const focusableElements = this.getFocusableElements(parentElement);
        const currentIndex = focusableElements.findIndex((el) => el === document.activeElement);
        if (focusableElements.length === 1) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.setFocusToFirstButton(parentElement, true);
        } else if (currentIndex === 0 && event.shiftKey) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.setFocusToFirstButton(parentElement, false);
        } else if (currentIndex >= focusableElements.length - 1 && !event.shiftKey) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.setFocusToFirstButton(parentElement, true);
        } else if (currentIndex === -1) {
          this.setFocusToFirstButton(parentElement, true);
        }
        break;
      }
    }
  }
  /**
   * Get all focusable elements within the modal
   * @param {DOM} modalElement The modal element
   * @returns {Array} An array of focusable elements
   */
  getFocusableElements(modalElement) {
    const focusableSelector = "button, a";
    return Array.from(modalElement.querySelectorAll(focusableSelector));
  }
  /**
   * Prevent scrolling on the document except for the modal
   * @param {Event} event The scroll event
   */
  preventScroll(event) {
    var _a;
    const isScrollableElement = (_a = event.target) == null ? void 0 : _a.closest(this.modalElement);
    if (!isScrollableElement || !this.isElementScrollable(isScrollableElement)) {
      event.preventDefault();
    } else {
      event.stopPropagation();
    }
  }
  /**
   * Fix scroll issues when modal is open
   * @param {DOM} modalElement The modal element
   */
  fixScrollLock(modalElement) {
    if (this.isWindows()) {
      this.applyWindowsScrollLock();
    } else {
      document.body.classList.add("is-scrollLock");
    }
    modalElement.addEventListener("scroll", this.handleScrollBug);
  }
  /**
   * Remove scroll lock fixes
   * @param {DOM} modalElement The modal element
   */
  removeScrollLockFix(modalElement) {
    if (this.isWindows()) {
      this.removeWindowsScrollLock();
    } else {
      document.body.classList.remove("is-scrollLock");
    }
    modalElement.removeEventListener("scroll", this.handleScrollBug);
  }
  /**
   * Check if the element is scrollable
   * @param {DOM} element The element to check
   * @returns {Boolean} True if the element is scrollable
   */
  isElementScrollable(element) {
    return element.clientHeight < element.scrollHeight;
  }
  /**
   * Handle scroll-related bugs in specific scenarios
   * @param {Event} event
   */
  handleScrollBug(event) {
    const element = event.target;
    if (!element) return;
    if (element.scrollTop + element.clientHeight === element.scrollHeight) {
      element.scrollTop -= 1;
    }
    if (element.scrollTop === 0) {
      element.scrollTop = 1;
    }
  }
  /**
   * Detect if the operating system is Windows
   * @returns {Boolean} True if the OS is Windows
   */
  isWindows() {
    return window.navigator.userAgent.toLowerCase().includes("windows nt");
  }
  /**
   * Apply scroll lock fix for Windows
   */
  applyWindowsScrollLock() {
    this.previousScrollPosition = document.documentElement.scrollTop || document.body.scrollTop;
    document.body.classList.add("is-scrollLock-windows");
    document.body.style.top = `-${this.previousScrollPosition}px`;
  }
  /**
   * Remove scroll lock fix for Windows
   */
  removeWindowsScrollLock() {
    document.body.classList.remove("is-scrollLock-windows");
    document.body.style.top = "initial";
    document.body.scrollTop = this.previousScrollPosition;
    document.documentElement.scrollTop = this.previousScrollPosition;
  }
}
customElements.define("original-modal", OriginalModal);
class OriginalSlider extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector(".js-slider");
    this.buttonPrev = this.querySelector(".js-sliderNavigationPrev");
    this.buttonNext = this.querySelector(".js-sliderNavigationNext");
    this.pagination = this.querySelector(".js-sliderPagination");
    this.sliderInstance = null;
  }
  connectedCallback() {
    const dataOptions = this.dataset.sliderOptions;
    if (dataOptions) {
      try {
        const options = JSON.parse(dataOptions);
        this.initializeSlider(options);
      } catch (error) {
        console.error("Failed to parse slider options:", error);
      }
    }
  }
  disconnectedCallback() {
    if (this.sliderInstance) {
      this.sliderInstance.destroy(true, true);
      this.sliderInstance = null;
    }
  }
  initializeSlider(sliderOptions) {
    if (!this.slider) {
      console.error("Slider element not found.");
      return;
    }
    if (this.sliderInstance) {
      this.sliderInstance.destroy(true, true);
    }
    this.sliderInstance = new Swiper(this.slider, {
      ...sliderOptions,
      ...this.buttonPrev && this.buttonNext ? {
        navigation: {
          nextEl: this.buttonNext,
          prevEl: this.buttonPrev
        }
      } : {},
      ...this.pagination ? {
        pagination: {
          el: this.pagination,
          clickable: true
        }
      } : {}
    });
  }
}
customElements.define("original-slider", OriginalSlider);
class OriginalVariantSelects extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);
      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues
        }
      });
    });
  }
  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;
    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options).find((option) => option.getAttribute("selected")).removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");
      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target.closest(".js-productFormInput").querySelector("[data-selected-value] > .js-swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", swatchValue);
        selectedDropdownSwatchValue.classList.remove("is-unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", "unset");
        selectedDropdownSwatchValue.classList.add("is-unavailable");
      }
      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset"
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target.closest(`.js-productFormInput`).querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }
  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }
  get selectedOptionValues() {
    return Array.from(this.querySelectorAll("select option[selected], fieldset input:checked")).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}
customElements.define("original-variant-selects", OriginalVariantSelects);
document.addEventListener("DOMContentLoaded", floatingMenuDisplayControl, false);
window.addEventListener("scroll", floatingMenuDisplayControl, false);
if (TO_TOP) {
  TO_TOP.addEventListener("click", toTop, false);
}
if (HAMBURGER) {
  HAMBURGER.addEventListener("click", drawerDisplayControl, false);
}
if (FULL_SCREEN_OVERLAY) {
  FULL_SCREEN_OVERLAY.addEventListener("click", drawerDisplayControl, false);
}
document.addEventListener("DOMContentLoaded", linkWithinPageControl, false);
